import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Icon,
  MetricCard,
  Tag,
  Tooltip,
} from '@makeably/creativex-design-system';
import {
  channelDimensionProps,
  renderSpendMetric,
} from 'components/creative_campaigns/shared';
import NestedItemsTable from 'components/molecules/NestedItemsTable';
import { toggleValue } from 'utilities/array';
import { getItemSortBy } from 'utilities/item';
import { rate } from 'utilities/number';
import {
  objectGroupBy,
  valueIf,
} from 'utilities/object';
import {
  toConciseCount,
  toConciseSpend,
  toPercent,
} from 'utilities/string';
import styles from './MediaBreakdown.module.css';

const recordProps = {
  adFormat: PropTypes.string,
  channel: PropTypes.string.isRequired,
  contentType: PropTypes.string,
  id: PropTypes.number.isRequired,
  impressions: PropTypes.number.isRequired,
  platformPlacements: PropTypes.arrayOf(PropTypes.string),
  spend: PropTypes.number.isRequired,
};

const propTypes = {
  filteredRecords: PropTypes.arrayOf(PropTypes.shape(recordProps)).isRequired,
  overallImpressions: PropTypes.number.isRequired,
  scoreDetails: PropTypes.shape({
    id: PropTypes.number,
    rateLabel: PropTypes.string,
    spendRateLabel: PropTypes.string,
  }).isRequired,
  supportedChannels: PropTypes.objectOf(channelDimensionProps).isRequired,
};

const BAR_WIDTH = 152;
const AD_FORMAT_OPTION = 'adFormat';
const CONTENT_TYPE_OPTION = 'contentType';
const PLACEMENT_OPTION = 'placement';

const viewOptions = [
  {
    label: 'Ad Format',
    value: AD_FORMAT_OPTION,
  },
  {
    label: 'Content Type',
    value: CONTENT_TYPE_OPTION,
  },
  {
    label: 'Placement',
    value: PLACEMENT_OPTION,
  },
];

const metrics = [
  {
    label: 'Channels',
    value: 'channels',
  },
  {
    label: 'Placements',
    value: PLACEMENT_OPTION,
  },
  {
    label: 'Ad Formats',
    value: AD_FORMAT_OPTION,
  },
  {
    label: 'Content Types',
    value: CONTENT_TYPE_OPTION,
  },
  {
    label: 'Impressions',
    value: 'impressions',
  },
];

function getHeaders(view, scoreDetails) {
  const allHeaders = [
    {
      key: 'channel',
      label: 'Channel',
    },
    {
      element: (
        <div className="u-flexRow u-alignCenter u-gap-8">
          Ad Format Usage
          <Tooltip label="Number of ad formats used out of total ad formats supported" />
        </div>
      ),
      filter: AD_FORMAT_OPTION,
      key: AD_FORMAT_OPTION,
      label: 'Ad Format Usage',
    },
    {
      element: (
        <div className="u-flexRow u-alignCenter u-gap-8">
          Content Type Usage
          <Tooltip label="Number of content types used out of total content types supported" />
        </div>
      ),
      filter: CONTENT_TYPE_OPTION,
      key: CONTENT_TYPE_OPTION,
      label: 'Content Type Usage',
    },
    {
      element: (
        <div className="u-flexRow u-alignCenter u-gap-8">
          Placement Usage
          <Tooltip label="Number of placements used out of total placements supported" />
        </div>
      ),
      filter: PLACEMENT_OPTION,
      key: PLACEMENT_OPTION,
      label: 'Placement Usage',
    },
    {
      key: 'spend',
      label: 'Spend',
    },
    {
      key: `scoreRate::${scoreDetails.id}`,
      label: scoreDetails.rateLabel,
    },
    {
      key: `spendRate::${scoreDetails.id}`,
      label: scoreDetails.spendRateLabel,
    },
    {
      key: 'expand',
      label: '',
      sortable: false,
    },
  ];

  return allHeaders.filter((header) => (header.filter ? header.filter === view : true));
}

function getSubHeaders(view, scoreDetails) {
  const allHeaders = [
    {
      filter: AD_FORMAT_OPTION,
      key: 'adFormat',
      label: 'Ad Format',
    },
    {
      filter: CONTENT_TYPE_OPTION,
      key: 'contentType',
      label: 'Content Type',
    },
    {
      filter: PLACEMENT_OPTION,
      key: 'placement',
      label: 'Placement',
    },
    {
      key: 'used',
      label: 'Used',
    },
    {
      key: 'spend',
      label: 'Spend',
    },
    {
      key: `scoreRate::${scoreDetails.id}`,
      label: scoreDetails.rateLabel,
    },
    {
      key: `spendRate::${scoreDetails.id}`,
      label: scoreDetails.spendRateLabel,
    },
  ];

  return allHeaders.filter((header) => (header.filter ? header.filter === view : true));
}

function getUsage(channel, supportedChannels, view, value) {
  const channelData = supportedChannels[channel];
  if (channelData[view]?.length > 0) {
    const total = channelData[view].length;
    const width = Math.round(BAR_WIDTH * (value / total));
    return {
      element: (
        <div className="u-flexRow u-alignCenter u-gap-8">
          <div className={styles.usageRatio}>
            { `${value}/${total}` }
          </div>
          <div className={styles.usageBar}>
            <div className={styles.usageBarValue} style={{ width: `${width}px` }} />
          </div>
        </div>
      ),
      value: value / total,
    };
  }

  const { label } = metrics.find((metric) => metric.value === view);
  return {
    element: (
      <Tag color="grey" label={`${label} Unsupported`} />
    ),
    value: -1,
  };
}

function getExpand(channel, supportedChannels, view, expandedChannels, setExpandedChannels) {
  const channelData = supportedChannels[channel];
  const button = (
    <Button
      iconLeft={expandedChannels.includes(channel) ? 'chevronDown' : 'chevronRight'}
      variant="round"
      onClick={() => setExpandedChannels(toggleValue(expandedChannels, channel))}
    />
  );

  return {
    ...valueIf(channelData[view]?.length > 0, 'element', button),
    value: '',
  };
}

function getChannelTotals(group, scoreId) {
  return group.reduce(
    ({
      spend,
      qualitySpend,
      qualityPostCount,
      postCount,
      adFormatSet,
      placementSet,
      creatorTypeSet,
    },
    record) => {
      const qualityPost = record[`rank::${scoreId}`] === 'highest';

      return {
        adFormatSet: record.adFormat ? new Set([...adFormatSet, record.adFormat]) : adFormatSet,
        creatorTypeSet:
          record.contentType ? new Set([...creatorTypeSet, record.contentType]) : creatorTypeSet,
        placementSet: new Set([...placementSet, ...(record.platformPlacements ?? [])]),
        postCount: postCount + (record.postCount ?? 0),
        qualityPostCount: qualityPostCount + (qualityPost ? record.postCount : 0),
        qualitySpend: qualitySpend + (record[`excellentSpend::${scoreId}`] ?? 0),
        spend: spend + (record.spend ?? 0),
      };
    },
    {
      adFormatSet: new Set(),
      creatorTypeSet: new Set(),
      placementSet: new Set(),
      postCount: 0,
      qualityPostCount: 0,
      qualitySpend: 0,
      spend: 0,
    },
  );
}

function getChannelItems(
  recordsByChannel,
  scoreId,
  view,
  supportedChannels,
  expandedChannels,
  setExpandedChannels,
) {
  return Object.values(recordsByChannel).map((group) => {
    const { channel } = group[0];

    const totals = getChannelTotals(group, scoreId);

    const scoreRate = rate(totals.qualityPostCount, totals.postCount);
    const spendRate = rate(totals.qualitySpend, totals.spend);

    return {
      [AD_FORMAT_OPTION]: getUsage(channel, supportedChannels, view, totals.adFormatSet.size),
      channel: { value: channel },
      [CONTENT_TYPE_OPTION]: getUsage(channel, supportedChannels, view, totals.creatorTypeSet.size),
      expand: getExpand(channel, supportedChannels, view, expandedChannels, setExpandedChannels),
      id: { value: channel },
      [PLACEMENT_OPTION]: getUsage(channel, supportedChannels, view, totals.placementSet.size),
      spend: {
        label: toConciseSpend(totals.spend),
        value: totals.spend,
      },
      [`scoreRate::${scoreId}`]: {
        label: toPercent(scoreRate) ?? 'N/A',
        value: scoreRate,
      },
      [`spendRate::${scoreId}`]: {
        label: toPercent(spendRate) ?? 'N/A',
        value: spendRate,
      },
    };
  });
}

function getViewTotals(group, scoreId) {
  return group.reduce(
    ({
      spend,
      qualitySpend,
      qualityPostCount,
      postCount,
    },
    record) => {
      const qualityPost = record[`rank::${scoreId}`] === 'highest';

      return {
        postCount: postCount + (record.postCount ?? 0),
        qualityPostCount: qualityPostCount + (qualityPost ? record.postCount : 0),
        qualitySpend: qualitySpend + (record[`excellentSpend::${scoreId}`] ?? 0),
        spend: spend + (record.spend ?? 0),
      };
    },
    {
      postCount: 0,
      qualityPostCount: 0,
      qualitySpend: 0,
      spend: 0,
    },
  );
}

function getUsedItems(records, view, unusedOptionsForChannel, scoreId) {
  return Object.values(records).map((group) => {
    let { [view]: value } = group[0];
    value = value || 'N/A';
    unusedOptionsForChannel.splice(unusedOptionsForChannel.indexOf(value), 1);

    const totals = getViewTotals(group, scoreId);

    const scoreRate = rate(totals.qualityPostCount, totals.postCount);
    const spendRate = rate(totals.qualitySpend, totals.spend);

    return {
      id: { value },
      spend: {
        label: toConciseSpend(totals.spend),
        value: totals.spend,
      },
      [`spendRate::${scoreId}`]: {
        label: toPercent(spendRate) ?? 'N/A',
        value: spendRate,
      },
      used: {
        element: (
          <div className="u-flexRow u-alignCenter u-gap-8">
            <Icon color="green" name="checkCircle" />
            Yes
          </div>
        ),
        value: true,
      },
      [view]: { value },
      [`scoreRate::${scoreId}`]: {
        label: toPercent(scoreRate) ?? 'N/A',
        value: scoreRate,
      },
    };
  });
}

function getSubItems(records, view, channelData, scoreId) {
  let recordsForLevel;
  if (view === PLACEMENT_OPTION) {
    recordsForLevel = records.filter((record) => record.level === 'placement');
  } else {
    recordsForLevel = records.filter((record) => record.level === 'post');
  }

  const groupedRecords = objectGroupBy(recordsForLevel, (record) => record[view]);
  const unusedOptionsForChannel = [...channelData[view]];
  const usedItems = getUsedItems(groupedRecords, view, unusedOptionsForChannel, scoreId);

  const unusedItems = unusedOptionsForChannel.map((value) => ({
    id: { value },
    [`scoreRate::${scoreId}`]: {
      label: 'N/A',
      value: 0,
    },
    spend: {
      label: toConciseSpend(0),
      value: 0,
    },
    [`spendRate::${scoreId}`]: {
      label: 'N/A',
      value: 0,
    },
    used: {
      element: (
        <div className="u-flexRow u-alignCenter u-gap-8">
          <Icon color="red" name="exclamationCircle" />
          No
        </div>
      ),
      value: false,
    },
    [view]: { value },
  }));

  return usedItems.concat(unusedItems);
}

function getOverallMetrics(supportedChannels, overallImpressions) {
  const channelTotals = Object.values(supportedChannels).reduce((total, channel) => ({
    adFormatSet: new Set([...total.adFormatSet, ...channel[AD_FORMAT_OPTION]]),
    contentTypeSet: new Set([...total.contentTypeSet, ...channel[CONTENT_TYPE_OPTION]]),
    placementSet: new Set([...total.placementSet, ...channel[PLACEMENT_OPTION]]),
  }), {
    adFormatSet: new Set(),
    contentTypeSet: new Set(),
    placementSet: new Set(),
  });

  return {
    [AD_FORMAT_OPTION]: channelTotals.adFormatSet.size,
    channels: Object.keys(supportedChannels).length,
    [CONTENT_TYPE_OPTION]: channelTotals.contentTypeSet.size,
    impressions: overallImpressions,
    [PLACEMENT_OPTION]: channelTotals.placementSet.size,
  };
}

function getMetricTotals(records) {
  return records.reduce(
    ({
      channelSet,
      placementSet,
      adFormatSet,
      contentTypeSet,
      impressions,
    }, record) => ({
      adFormatSet: record.adFormat ? new Set([...adFormatSet, record.adFormat]) : adFormatSet,
      channelSet: record.channel ? new Set([...channelSet, record.channel]) : channelSet,
      contentTypeSet:
        record.contentType ? new Set([...contentTypeSet, record.contentType]) : contentTypeSet,
      impressions: impressions + (record.impressions ?? 0),
      placementSet: new Set([...placementSet, ...(record.platformPlacements ?? [])]),
    }), {
      adFormatSet: new Set(),
      channelSet: new Set(),
      contentTypeSet: new Set(),
      impressions: 0,
      placementSet: new Set(),
    },
  );
}

function calculateMetrics(records) {
  const totals = getMetricTotals(records);
  return {
    [AD_FORMAT_OPTION]: totals.adFormatSet.size,
    channels: totals.channelSet.size,
    [CONTENT_TYPE_OPTION]: totals.contentTypeSet.size,
    impressions: totals.impressions,
    [PLACEMENT_OPTION]: totals.placementSet.size,
  };
}

function getMetricValue(metric, filteredRecords, filteredMetrics) {
  if (!filteredRecords.length) return 'No data';
  if (metric.value === 'impressions') return toConciseCount(filteredMetrics.impressions);
  return filteredMetrics[metric.value];
}

function renderEmptyContent() {
  return (
    <div className="t-empty">
      <p>No data available</p>
    </div>
  );
}

function MediaBreakdown({
  filteredRecords,
  overallImpressions,
  scoreDetails,
  supportedChannels,
}) {
  const [view, setView] = useState(viewOptions[0]);
  const [expandedChannels, setExpandedChannels] = useState([]);
  const [sort, setSort] = useState({
    asc: false,
    key: viewOptions[0],
  });
  const [subSort, setSubSort] = useState({
    asc: false,
    key: 'spend',
  });

  const overallMetrics = getOverallMetrics(supportedChannels, overallImpressions);

  const filteredPostRecords = useMemo(() => (
    filteredRecords.filter((record) => record.level === 'post')
  ), [filteredRecords]);

  const filteredMetrics = useMemo(() => (
    calculateMetrics(filteredPostRecords)
  ), [filteredPostRecords]);

  const postRecordsByChannel = useMemo(() => (
    objectGroupBy(filteredPostRecords, (record) => record.channel)
  ), [filteredPostRecords]);

  const recordsByChannel = useMemo(() => (
    objectGroupBy(filteredRecords, (record) => record.channel)
  ), [filteredRecords]);

  const items = useMemo(() => (
    getChannelItems(
      postRecordsByChannel,
      scoreDetails.id,
      view.value,
      supportedChannels,
      expandedChannels,
      setExpandedChannels,
    )
  ), [recordsByChannel, scoreDetails, view, expandedChannels]);

  const sortedItems = useMemo(() => {
    const byKeyDir = getItemSortBy(sort.key, sort.asc);
    return items.slice().sort(byKeyDir);
  }, [items, sort]);

  const subItemsByChannel = useMemo(() => {
    const mappedItems = {};
    Object.values(recordsByChannel).forEach((group) => {
      const { channel } = group[0];
      const channelData = supportedChannels[channel];
      if (channelData[view.value]) {
        const subItems = getSubItems(group, view.value, channelData, scoreDetails.id);
        const byKeyDir = getItemSortBy(subSort.key, subSort.asc);
        mappedItems[channel] = subItems.slice().sort(byKeyDir);
      }
    });
    return mappedItems;
  }, [recordsByChannel, scoreDetails, view]);

  const sortedSubItems = useMemo(() => {
    const mappedItems = {};
    Object.entries(subItemsByChannel).forEach(([channel, subItems]) => {
      const byKeyDir = getItemSortBy(subSort.key, subSort.asc);
      mappedItems[channel] = subItems.slice().sort(byKeyDir);
    });
    return mappedItems;
  }, [subItemsByChannel, subSort]);

  const channelSpends = useMemo(() => {
    const itemSpends = items.map((item) => item.spend.value || 0);
    return {
      max: itemSpends.length > 0 ? Math.max(...itemSpends) : null,
      min: itemSpends.length > 0 ? Math.min(...itemSpends) : null,
    };
  }, [items]);

  useEffect(() => {
    setExpandedChannels([]);
    setSort({
      asc: false,
      key: view.value,
    });
  }, [view]);

  const renderMetricCard = (metric) => {
    const value = getMetricValue(metric, filteredRecords, filteredMetrics);
    const caption = `out of ${toConciseCount(overallMetrics[metric.value])} total`;

    return (
      <div key={metric.value} className={styles.metricCard}>
        <MetricCard
          alert={false}
          metric={{
            ...metric,
            caption,
            value,
          }}
        />
      </div>
    );
  };

  const renderMetricCards = () => {
    if (filteredRecords.length > 0) {
      return metrics.map(renderMetricCard);
    }
    return renderEmptyContent();
  };

  return (
    <div className="u-flexColumn u-gap-16">
      <Card className="u-flexColumn u-gap-16">
        <h5>Media Execution</h5>
        <div className="u-flexRow u-justifyBetween">
          { renderMetricCards() }
        </div>
      </Card>
      <Card className="u-flexColumn u-gap-24">
        <div className="u-flexRow u-justifyBetween">
          <div className="u-flexColumn u-gap-24">
            <h5>{ `Channel x ${view.label}` }</h5>
            <div className="u-flexRow u-gap-24">
              { renderSpendMetric('Lowest Channel Spend', channelSpends.min) }
              { renderSpendMetric('Highest Channel Spend', channelSpends.max) }
            </div>
          </div>
          <Dropdown
            label="View by:"
            menuProps={{ size: 'medium' }}
            options={viewOptions}
            selected={view}
            size="medium"
            onChange={(opt) => setView(opt)}
          />
        </div>
        <Divider />
        <NestedItemsTable
          className={styles.table}
          emptyTableContent={renderEmptyContent()}
          expandedIds={expandedChannels}
          headers={getHeaders(view.value, scoreDetails)}
          items={sortedItems}
          sort={sort}
          subHeaders={getSubHeaders(view.value, scoreDetails)}
          subItems={sortedSubItems}
          subSort={subSort}
          onSortChange={setSort}
          onSubSortChange={setSubSort}
        />
      </Card>
    </div>
  );
}

MediaBreakdown.propTypes = propTypes;

export default MediaBreakdown;
